import { useQuery } from '@tanstack/react-query';
import { checkRestrictedAccess } from 'services/offerings';
import { canViewRestrictedOffering } from 'lib/restrictedOfferingAccess';

/**
 * Hook to check if a user has restricted access to an offering.
 *
 * Restriction types:
 *   PUBLIC              – anyone can access (no check needed)
 *   AUTHENTICATED_USERS – any signed-in user can access (no allowlist check)
 *   ALLOWLIST_ONLY      – only users on the explicit allowlist can access
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.offeringId - The ID of the offering to check access for
 * @param {string} params.primaryUserId - The primary user ID
 * @param {Array} params.userRoles - The user's roles array
 * @param {string} params.restrictionType - The offering's restriction type
 * @param {boolean} params.enabled - Whether the query should be enabled (default: true if both IDs are provided)
 * @returns {Object} Query result with data, isLoading, isError, etc.
 */
const useCheckRestrictedAccess = ({
  offeringId,
  primaryUserId,
  userRoles = [],
  restrictionType,
  enabled = !!(offeringId && primaryUserId),
}) => {
  // Check if user has a role that can always access restricted offerings
  const hasRestrictedAccessRole = canViewRestrictedOffering(userRoles);

  // AUTHENTICATED_USERS mode: any signed-in user has access, no API call needed
  const isAuthenticatedUsersMode = restrictionType === 'AUTHENTICATED_USERS';

  const skipApiCall = hasRestrictedAccessRole || isAuthenticatedUsersMode;

  const { data, isLoading, isError, isSuccess, error, refetch } = useQuery({
    queryKey: ['restrictedAccess', offeringId, primaryUserId],
    queryFn: () => checkRestrictedAccess({ offeringId, primaryUserId }),
    enabled: enabled && !skipApiCall,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
  });

  // Users with restricted-access roles and authenticated-users-mode always have access when signed in
  const hasAccess = skipApiCall ? true : data?.hasAccess || false;

  return {
    hasAccess,
    data: skipApiCall ? { hasAccess: true } : data,
    isLoading: skipApiCall ? false : isLoading,
    isError: skipApiCall ? false : isError,
    isSuccess: skipApiCall ? true : isSuccess,
    error: skipApiCall ? null : error,
    refetch,
  };
};

export default useCheckRestrictedAccess;
